import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Card, Row, Col, Button, Form, InputGroup, Badge, Spinner } from 'react-bootstrap';
import FalconCloseButton from 'components/common/FalconCloseButton';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';

const PER_PAGE = 10;

/**
 * Modal para mostrar la lista de productos y permitir agregarlos al carrito.
 * Los productos se buscan server-side con debounce y paginación.
 */
const ModalProductos = ({
    show,
    onHide,
    token,
    moneda,
    onAgregarProducto
}) => {
    const [searchTerm, setSearchTerm]         = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [productos, setProductos]           = useState([]);
    const [loading, setLoading]               = useState(false);
    const [pagina, setPagina]                 = useState(1);
    const [total, setTotal]                   = useState(0);

    // Debounce 300 ms + reset de página
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPagina(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Limpiar estado al cerrar el modal
    useEffect(() => {
        if (!show) {
            setSearchTerm('');
            setDebouncedSearch('');
            setPagina(1);
            setProductos([]);
            setTotal(0);
        }
    }, [show]);

    // Fetch cada vez que el modal está abierto y cambia: búsqueda, página o moneda
    useEffect(() => {
        if (!show) return;

        let cancelled = false;

        const fetchProductos = async () => {
            setLoading(true);
            try {
                const res = await fetch('/api/orden/BuscarProductos', {
                    method: 'POST',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        vMoneda:         moneda || 'MXN',
                        vBusqueda:       debouncedSearch || null,
                        iPagina:         pagina,
                        iFilasPorPagina: PER_PAGE
                    })
                });

                if (!res.ok || cancelled) return;

                const data = await res.json();
                if (cancelled) return;

                if (data.iResponseType === 1) {
                    const parsed = JSON.parse(data.detalles);
                    setTotal(parsed.table2?.[0]?.iTotal ?? 0);
                    setProductos(parsed.table3 ?? []);
                } else {
                    setProductos([]);
                    setTotal(0);
                }
            } catch (e) {
                if (!cancelled) console.error('Error al buscar productos:', e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchProductos();
        return () => { cancelled = true; };
    }, [show, debouncedSearch, pagina, moneda]);

    const totalPages = Math.max(Math.ceil(total / PER_PAGE), 1);

    const columns = [
        {
            accessor: 'none',
            Header: '',
            disableSortBy: true,
            Cell: rowData => (
                <Row className="flex-end-center">
                    <Col xs="auto">
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => onAgregarProducto(rowData.row.original)}
                        >
                            +
                        </Button>
                    </Col>
                </Row>
            )
        },
        {
            accessor: 'idProducto',
            Header: 'ID',
            cellProps: { className: 'ps-1' },
            Cell: rowData => <b>{rowData.row.original.idProducto}</b>
        },
        {
            accessor: 'vCodigoBarras',
            Header: 'Codigo Barras',
            cellProps: { className: 'ps-1' },
            Cell: rowData => <b>{rowData.row.original.vCodigoBarras}</b>
        },
        {
            accessor: 'vNombreProducto',
            Header: 'Nombre del producto',
            cellProps: { className: 'ps-1' },
            Cell: rowData => {
                const { vNombreProducto } = rowData.row.original;
                return <span title={vNombreProducto} className="text-wrap">{vNombreProducto}</span>;
            }
        },
        {
            accessor: 'dTotal',
            Header: 'Total',
            cellProps: { className: 'ps-1 text-center' },
            headerProps: { className: 'text-center' },
            Cell: rowData => {
                const { dTotal, vMoneda: vM } = rowData.row.original;
                return (
                    <code>
                        ${parseFloat(dTotal).toLocaleString('es-MX', {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2
                        })} {vM}
                    </code>
                );
            }
        }
    ];

    const inicio = total === 0 ? 0 : (pagina - 1) * PER_PAGE + 1;
    const fin    = Math.min(pagina * PER_PAGE, total);

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            backdrop="static"
            aria-labelledby="modal-productos-title"
        >
            <Modal.Header>
                <Modal.Title id="modal-productos-title" className="d-flex align-items-center gap-2">
                    Lista de productos &mdash; Pago &Uacute;nico
                    {moneda && (
                        <Badge bg="primary" className="ms-1 fs--2 fw-normal">
                            {moneda}
                        </Badge>
                    )}
                </Modal.Title>
                <FalconCloseButton
                    className="btn btn-circle btn-sm transition-base p-0"
                    onClick={onHide}
                />
            </Modal.Header>
            <Modal.Body style={{ padding: '0px' }}>
                <AdvanceTableWrapper
                    columns={columns}
                    data={productos}
                    sortable
                    pagination
                    perPage={PER_PAGE}
                >
                    <Card>
                        <Card.Header className="py-2 px-3">
                            <Row className="align-items-center g-2">
                                <Col sm={8} lg={7}>
                                    <InputGroup size="sm">
                                        <Form.Control
                                            type="search"
                                            placeholder="Buscar por nombre, código de barras o ID..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            autoFocus
                                        />
                                        {loading && (
                                            <InputGroup.Text>
                                                <Spinner animation="border" size="sm" />
                                            </InputGroup.Text>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col xs="auto" className="ms-auto">
                                    <span className="text-muted fs--2">
                                        {total === 0
                                            ? 'Sin resultados'
                                            : `${inicio}–${fin} de ${total} productos`}
                                    </span>
                                </Col>
                            </Row>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <AdvanceTable
                                table
                                headerClassName="bg-200 text-900 text-nowrap align-middle"
                                rowClassName="align-middle white-space-nowrap"
                                tableProps={{
                                    size: 'sm',
                                    striped: true,
                                    hover: true,
                                    className: 'fs--1 mb-0 overflow-hidden'
                                }}
                            />
                        </Card.Body>
                        <Card.Footer className="d-flex align-items-center justify-content-between py-2">
                            <Button
                                size="sm"
                                variant="outline-secondary"
                                disabled={pagina <= 1 || loading}
                                onClick={() => setPagina(p => p - 1)}
                            >
                                &#8249; Anterior
                            </Button>
                            <span className="fs--2 text-muted">
                                P&aacute;gina {pagina} / {totalPages}
                            </span>
                            <Button
                                size="sm"
                                variant="outline-secondary"
                                disabled={pagina >= totalPages || loading}
                                onClick={() => setPagina(p => p + 1)}
                            >
                                Siguiente &#8250;
                            </Button>
                        </Card.Footer>
                    </Card>
                </AdvanceTableWrapper>
            </Modal.Body>
        </Modal>
    );
};

ModalProductos.propTypes = {
    show:              PropTypes.bool.isRequired,
    onHide:            PropTypes.func.isRequired,
    token:             PropTypes.string.isRequired,
    moneda:            PropTypes.string,
    onAgregarProducto: PropTypes.func.isRequired
};

export default ModalProductos;

